/**
 * `/knowledge/drafts` — operator review queue for KB drafts the
 * repo-manager DO proposed (spec §5.8 feedback loop).
 *
 * Loader + action go through the Worker REST routes (commit 4), not
 * Supabase directly: the Worker's `kb:sync` permission gate is the
 * single source of truth for who can flip a draft to active, and
 * RLS readability for non-active rows is admin-only anyway.
 */

import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
} from 'react-router';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  KbSeverityBadge,
  Label,
  Textarea,
} from '@repo-manager/ui';

import { env } from '../lib/env';
import { getSupabase } from '../lib/supabase';

interface KbDraftRow {
  id: string;
  slug: string;
  title: string;
  type: 'pattern' | 'standard' | 'reference_impl' | 'lesson';
  severity: 'standard' | 'recommended' | 'optional';
  summary: string;
  content: string;
  rationale: string | null;
  proposed_by: string | null;
  created_at: string;
  stack_layers: string[] | null;
  technologies: string[] | null;
}

async function bearerToken(): Promise<string> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Response('Not authenticated', { status: 401 });
  return token;
}

export async function kbDraftsLoader(): Promise<{ drafts: KbDraftRow[]; error?: string }> {
  try {
    const token = await bearerToken();
    const res = await fetch(`${env.VITE_WORKER_URL}/api/kb/drafts`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      return {
        drafts: [],
        error: `worker_${res.status}: ${(await res.text()).slice(0, 200)}`,
      };
    }
    const json = (await res.json()) as { drafts: KbDraftRow[] };
    return { drafts: json.drafts ?? [] };
  } catch (err) {
    return {
      drafts: [],
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

interface ActionResult {
  ok: boolean;
  slug?: string;
  intent?: 'approve' | 'reject';
  error?: string;
}

export async function kbDraftsAction({
  request,
}: {
  request: Request;
}): Promise<ActionResult> {
  const fd = await request.formData();
  const intent = String(fd.get('intent') ?? '');
  const slug = String(fd.get('slug') ?? '');
  if (!slug) return { ok: false, error: 'missing_slug' };

  let path: string;
  let body: Record<string, string> = {};
  if (intent === 'approve') {
    path = `/api/kb/drafts/${encodeURIComponent(slug)}/approve`;
  } else if (intent === 'reject') {
    const reason = String(fd.get('reason') ?? '').trim();
    if (!reason) return { ok: false, error: 'missing_reason' };
    path = `/api/kb/drafts/${encodeURIComponent(slug)}/reject`;
    body = { reason };
  } else {
    return { ok: false, error: `unknown_intent:${intent}` };
  }

  try {
    const token = await bearerToken();
    const res = await fetch(`${env.VITE_WORKER_URL}${path}`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      return {
        ok: false,
        slug,
        intent: intent as 'approve' | 'reject',
        error: `worker_${res.status}: ${(await res.text()).slice(0, 200)}`,
      };
    }
    return { ok: true, slug, intent: intent as 'approve' | 'reject' };
  } catch (err) {
    return {
      ok: false,
      slug,
      intent: intent as 'approve' | 'reject',
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

export function KbDraftsPage() {
  const { drafts, error } = useLoaderData() as {
    drafts: KbDraftRow[];
    error?: string;
  };
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const submitting = navigation.state === 'submitting';

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">KB drafts</h1>
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">KB drafts</h1>
          <p className="text-sm text-muted-foreground">
            Drafts the repo-manager proposed (spec §5.8). Approve to flip to{' '}
            <code>active</code>; reject to flip to <code>deprecated</code> with a
            reason recorded for the audit trail.
          </p>
        </div>
        <Link to="/knowledge">
          <Button variant="ghost">← Back to KB</Button>
        </Link>
      </div>

      {actionData?.ok && (
        <Card className="border-emerald-500/30 bg-emerald-50/20">
          <CardContent className="py-3 text-sm">
            ✓ {actionData.intent === 'reject' ? 'Rejected' : 'Approved'}{' '}
            <code>{actionData.slug}</code>
          </CardContent>
        </Card>
      )}
      {actionData?.ok === false && actionData.error && (
        <Card className="border-destructive/30 bg-destructive/10">
          <CardContent className="py-3 text-sm text-destructive">
            ✗ {actionData.error}
          </CardContent>
        </Card>
      )}

      {drafts.length === 0 ? (
        <EmptyState
          title="No drafts pending"
          description="The repo-manager hasn't proposed any new KB entries since the last review."
        />
      ) : (
        <div className="space-y-3">
          {drafts.map((d) => (
            <DraftCard key={d.id} draft={d} submitting={submitting} />
          ))}
        </div>
      )}
    </div>
  );
}

function DraftCard({
  draft,
  submitting,
}: {
  draft: KbDraftRow;
  submitting: boolean;
}) {
  const [rejectReason, setRejectReason] = useState('');
  const ageMs = Date.now() - new Date(draft.created_at).getTime();
  const ageMin = Math.max(0, Math.floor(ageMs / 60_000));
  const ageStr =
    ageMin < 60
      ? `${ageMin}m ago`
      : ageMin < 60 * 24
        ? `${Math.floor(ageMin / 60)}h ago`
        : `${Math.floor(ageMin / 60 / 24)}d ago`;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-lg">
              <code className="font-mono text-sm text-muted-foreground">
                {draft.slug}
              </code>{' '}
              · {draft.title}
            </CardTitle>
            <CardDescription>{draft.summary}</CardDescription>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant="secondary">{draft.type}</Badge>
            <KbSeverityBadge severity={draft.severity} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs text-muted-foreground">
          Proposed by <code>{draft.proposed_by ?? 'unknown'}</code> · {ageStr}
          {draft.stack_layers && draft.stack_layers.length > 0 && (
            <> · layers: {draft.stack_layers.join(', ')}</>
          )}
        </div>
        {draft.rationale && (
          <div className="rounded-md bg-muted/40 p-3 text-sm">
            <strong>Rationale:</strong> {draft.rationale}
          </div>
        )}
        <details>
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
            Full content
          </summary>
          <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-muted/40 p-3 text-xs">
            {draft.content}
          </pre>
        </details>
        <div className="flex justify-end gap-2 pt-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={submitting}>
                Reject
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reject draft “{draft.slug}”?</DialogTitle>
                <DialogDescription>
                  The reason is stored on <code>rationale</code> for the audit
                  trail. The entry flips to <code>deprecated</code>.
                </DialogDescription>
              </DialogHeader>
              <Form method="post" className="space-y-3">
                <input type="hidden" name="intent" value="reject" />
                <input type="hidden" name="slug" value={draft.slug} />
                <div className="space-y-2">
                  <Label htmlFor={`reason-${draft.id}`}>Reason</Label>
                  <Textarea
                    id={`reason-${draft.id}`}
                    name="reason"
                    required
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Already covered by kb:foo, or wrong stack layer, etc."
                  />
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="ghost">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={submitting || rejectReason.trim().length === 0}
                  >
                    Reject draft
                  </Button>
                </DialogFooter>
              </Form>
            </DialogContent>
          </Dialog>
          <Form method="post">
            <input type="hidden" name="intent" value="approve" />
            <input type="hidden" name="slug" value={draft.slug} />
            <Button type="submit" size="sm" disabled={submitting}>
              Approve
            </Button>
          </Form>
        </div>
      </CardContent>
    </Card>
  );
}
